/**
 * Admin Home primary actions — only real, authorized destinations/components.
 * Create Workspace / Invite Member open existing modals; Templates and
 * Governance Exceptions navigate to established routes. No fake counts.
 */
import { Link } from "react-router-dom";
import {
  FolderPlus,
  LayoutTemplate,
  ShieldCheck,
  UserPlus,
} from "lucide-react";

import {
  ADMIN_HOME_ACTION_IDS,
  type AdminHomeActionId,
} from "../adminHomeLayout";

import { Button } from "@/components/ui/button/Button";
import { Card, CardBody, CardHeader } from "@/components/ui/card/Card";
import { spacing, typography } from "@/design";
import { cn } from "@/lib/utils";


export const ADMIN_HOME_GOVERNANCE_EXCEPTIONS_HREF =
  "/administration/governance?tab=exceptions";

export type AdminHomePrimaryActionsProps = {
  onCreateWorkspace: () => void;
  onInviteMember: () => void;
  /** Actions the user chose to show. Omitted means all of them. */
  visibleActions?: readonly AdminHomeActionId[];
};

const navActionClass = cn(
  "inline-flex h-10 items-center justify-center gap-2 rounded-md border border-input bg-white px-4 text-sm font-medium text-slate-900",
  "hover:bg-accent hover:text-accent-foreground",
  "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2",
);

export function AdminHomePrimaryActions({
  onCreateWorkspace,
  onInviteMember,
  visibleActions = ADMIN_HOME_ACTION_IDS,
}: AdminHomePrimaryActionsProps) {
  const show = (id: AdminHomeActionId): boolean => visibleActions.includes(id);
  // Nothing to offer: omit the card rather than render an empty one.
  if (visibleActions.length === 0) return null;
  return (
    <Card data-testid="admin-home-primary-actions" aria-label="Primary actions">
      <CardHeader className="space-y-1 p-5 pb-3">
        <h2 className={typography.sectionTitle}>Quick actions</h2>
        <p className={typography.muted}>
          Start from established organization workflows.
        </p>
      </CardHeader>
      <CardBody className="p-5 pt-0">
        <div className={cn("flex flex-wrap", spacing.inlineGap)}>
          {show("create_workspace") && (
            <Button
              type="button"
              variant="primary"
              size="md"
              onClick={onCreateWorkspace}
              iconLeft={<FolderPlus className="h-4 w-4" aria-hidden />}
              data-testid="admin-home-action-create-workspace"
            >
              Create Workspace
            </Button>
          )}
          {show("template_center") && (
            <Link
              to="/templates"
              className={navActionClass}
              data-testid="admin-home-action-templates"
            >
              <LayoutTemplate className="h-4 w-4 shrink-0" aria-hidden />
              Template Center
            </Link>
          )}
          {show("governance_gate") && (
            <Link
              to={ADMIN_HOME_GOVERNANCE_EXCEPTIONS_HREF}
              className={navActionClass}
              data-testid="admin-home-action-governance"
            >
              <ShieldCheck className="h-4 w-4 shrink-0" aria-hidden />
              Governance exceptions
            </Link>
          )}
          {show("invite_member") && (
            <Button
              type="button"
              variant="outline"
              size="md"
              onClick={onInviteMember}
              iconLeft={<UserPlus className="h-4 w-4" aria-hidden />}
              data-testid="admin-home-action-invite"
            >
              Invite Member
            </Button>
          )}
        </div>
      </CardBody>
    </Card>
  );
}
